import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';
import { UFS } from '../constants';
import { rolarParaPrimeiroErro } from '../utils';
import type { EstadoSalvamento, EventoDraft, ImagemDraft } from '../types';

export interface BlocoBasicoHandle {
  validar: () => boolean;
  focar: (campo: string) => void;
}

interface BlocoBasicoProps {
  draft: EventoDraft;
  eventoId: number | null;
  onChange: (patch: Partial<EventoDraft>) => void;
  perfilCidade: string;
  perfilUf: string;
  estadoSalvamento: EstadoSalvamento;
  /** Ajusta o texto para a porta "Vender ingresso" (sem jargão de acampamento/inscrição). */
  ehIngresso?: boolean;
}

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 5 * 1024 * 1024;

const inputCls =
  'mt-1 w-full rounded-md border bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

function BlocoBasico(
  { draft, eventoId, onChange, perfilCidade, perfilUf, estadoSalvamento, ehIngresso }: BlocoBasicoProps,
  ref: React.Ref<BlocoBasicoHandle>,
) {
  const [erros, setErros] = useState<Record<string, string>>({});
  const [mostrarAjustes, setMostrarAjustes] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputArteRef = useRef<HTMLInputElement>(null);

  const temPerfil = perfilCidade.trim() !== '' || perfilUf !== '';
  const doPerfil =
    temPerfil && draft.cidade.trim() === perfilCidade.trim() && draft.uf === perfilUf;

  useImperativeHandle(ref, () => ({
    validar() {
      const novos: Record<string, string> = {};

      if (!draft.nome.trim()) novos.nome = 'Digite o nome do evento.';
      if (!draft.cidade.trim()) novos.cidade = 'Digite a cidade.';
      if (!draft.uf) novos.uf = 'Escolha o estado.';
      if (!draft.inicioData) novos.inicioData = 'Escolha a data de início.';
      if (!draft.inicioHora) novos.inicioHora = 'Escolha o horário de início.';

      if (draft.inicioData && draft.inicioHora && draft.fimData && draft.fimHora) {
        const inicio = new Date(`${draft.inicioData}T${draft.inicioHora}:00`).getTime();
        const fim = new Date(`${draft.fimData}T${draft.fimHora}:00`).getTime();
        if (fim <= inicio) novos.fimData = 'O término precisa ser depois do início.';
      }

      setErros(novos);
      if (Object.keys(novos).length > 0) {
        rolarParaPrimeiroErro();
        return false;
      }
      return true;
    },
    focar(campo: string) {
      const precisaExpandir = campo === 'descricao' || campo === 'endereco' || campo === 'cep';
      if (precisaExpandir) setMostrarAjustes(true);

      const focarEl = () => {
        const el = document.getElementById(campo);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          el instanceof HTMLSelectElement
        ) {
          el.focus();
        }
      };

      if (precisaExpandir) requestAnimationFrame(focarEl);
      else focarEl();
    },
  }));

  function mudar(campo: keyof EventoDraft, valor: string) {
    onChange({ [campo]: valor } as Partial<EventoDraft>);
    if (erros[campo]) setErros((e) => ({ ...e, [campo]: '' }));
  }

  function aoEscolherArte(arquivo: File | null) {
    if (!arquivo) return;
    if (!EXTENSOES_OK.includes(arquivo.type)) return;
    if (arquivo.size > TAMANHO_MAX) return;
    const url = URL.createObjectURL(arquivo);
    const nova: ImagemDraft = { arquivo, url, removida: false, carregando: false };
    onChange({ imagemDesktop: nova, imagemMobile: nova });
  }

  function aoSoltar(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setArrastando(false);
    aoEscolherArte(e.dataTransfer.files?.[0] ?? null);
  }

  function removerArte() {
    const atual = draft.imagemDesktop;
    if (atual.arquivo && atual.url) URL.revokeObjectURL(atual.url);
    const limpa: ImagemDraft = { arquivo: null, url: null, removida: true, carregando: false };
    onChange({ imagemDesktop: limpa, imagemMobile: limpa });
    if (inputArteRef.current) inputArteRef.current.value = '';
  }

  const arteUrl = draft.imagemDesktop.url;

  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">Sobre o evento</h2>
        <p className="mt-1 text-sm text-secondary-500">
          O básico: nome, quando e onde acontece.
        </p>
      </div>

      {/* Nome */}
      <CampoTexto
        id="nome"
        name="nome"
        label="Nome do evento"
        help={
          ehIngresso
            ? 'Digite o nome que será exibido ao público. Ex.: Luau da Juventude.'
            : 'Digite o nome que será exibido ao público. Ex.: Acampamento Juvenil de Tupã 2026.'
        }
        value={draft.nome}
        onChange={(v) => mudar('nome', v)}
        required
        error={erros.nome || null}
        placeholder={ehIngresso ? 'Luau da Juventude' : 'Acampamento Juvenil de Tupã 2026'}
        autoFocus={eventoId === null}
      />

      {/* Quando */}
      <div>
        <span className="block text-base font-medium text-foreground-950">
          Quando <span className="text-danger-600">*</span>
        </span>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-secondary-200 bg-white p-4">
            <label className="block text-sm font-medium text-foreground-800">Começa</label>
            <div className="mt-2 flex gap-2">
              <input
                id="inicioData"
                type="date"
                value={draft.inicioData}
                onChange={(e) => mudar('inicioData', e.target.value)}
                className={`${inputCls} cursor-pointer ${erros.inicioData ? 'border-danger-500' : 'border-secondary-300'}`}
              />
              <input
                id="inicioHora"
                type="time"
                value={draft.inicioHora}
                onChange={(e) => mudar('inicioHora', e.target.value)}
                className={`${inputCls} cursor-pointer ${erros.inicioHora ? 'border-danger-500' : 'border-secondary-300'}`}
              />
            </div>
            {(erros.inicioData || erros.inicioHora) && (
              <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                {erros.inicioData || erros.inicioHora}
              </p>
            )}
          </div>

          <div className="rounded-lg border border-secondary-200 bg-white p-4">
            <label className="block text-sm font-medium text-foreground-800">Termina</label>
            <div className="mt-2 flex gap-2">
              <input
                type="date"
                value={draft.fimData}
                onChange={(e) => mudar('fimData', e.target.value)}
                className={`${inputCls} cursor-pointer ${erros.fimData ? 'border-danger-500' : 'border-secondary-300'}`}
              />
              <input
                type="time"
                value={draft.fimHora}
                onChange={(e) => mudar('fimHora', e.target.value)}
                className={`${inputCls} cursor-pointer ${erros.fimHora ? 'border-danger-500' : 'border-secondary-300'}`}
              />
            </div>
            {erros.fimData && (
              <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                {erros.fimData}
              </p>
            )}
          </div>
        </div>
        <p className="mt-2 text-sm text-secondary-500">
          Se não puser fim, a gente considera 3 horas.
        </p>
      </div>

      {/* Onde */}
      <CampoTexto
        id="local"
        name="local"
        label="Onde"
        help="O nome do lugar, como as pessoas conhecem. Ex.: Paróquia São João Batista."
        value={draft.localNome}
        onChange={(v) => mudar('localNome', v)}
        placeholder="Paróquia São João Batista"
      />

      {/* Cidade / UF */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="block text-base font-medium text-foreground-950">
            Cidade / UF <span className="text-danger-600">*</span>
          </span>
          {temPerfil && (
            <span
              className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
                doPerfil ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-600'
              }`}
            >
              {doPerfil ? 'do seu perfil' : 'alterado para este evento'}
            </span>
          )}
        </div>
        <div
          className={`mt-2 grid grid-cols-[1fr_110px] gap-2 rounded-lg border p-2 ${
            doPerfil ? 'border-primary-200 bg-primary-50' : 'border-secondary-200 bg-white'
          }`}
        >
          <input
            id="cidade"
            type="text"
            value={draft.cidade}
            onChange={(e) => mudar('cidade', e.target.value)}
            placeholder="Tupã"
            className={`w-full border-0 bg-transparent px-2 py-2 text-[15px] text-foreground-950 outline-none placeholder:text-secondary-400 ${
              erros.cidade ? 'text-danger-600' : ''
            }`}
          />
          <select
            value={draft.uf}
            onChange={(e) => mudar('uf', e.target.value)}
            className={`cursor-pointer border-0 bg-transparent px-1 py-2 text-[15px] text-foreground-950 outline-none ${erros.uf ? 'text-danger-600' : ''}`}
          >
            <option value="">UF</option>
            {UFS.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </div>
        {(erros.cidade || erros.uf) && (
          <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
            {erros.cidade || erros.uf}
          </p>
        )}
      </div>

      {/* Arte de divulgação */}
      <div id="arte">
        <span className="block text-base font-medium text-foreground-950">A arte de divulgação</span>
        <p className="mt-1 text-sm text-secondary-500">
          Opcional para salvar, obrigatória para publicar. Sem ela, o evento fica com uma pendência.
        </p>

        <input
          ref={inputArteRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="sr-only"
          onChange={(e) => aoEscolherArte(e.target.files?.[0] ?? null)}
        />

        {arteUrl ? (
          <div className="mt-2 overflow-hidden rounded-lg border border-secondary-200 bg-background-100">
            <div className="relative">
              <img
                src={arteUrl}
                alt="Arte de divulgação do evento"
                className="aspect-video w-full object-top"
              />
              <button
                type="button"
                onClick={removerArte}
                aria-label="Remover arte"
                className="absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputArteRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputArteRef.current?.click();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={aoSoltar}
            className={`mt-2 flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-white px-4 py-8 text-center transition-colors ${
              arrastando
                ? 'border-primary-500 bg-primary-50'
                : 'border-secondary-300 hover:border-primary-400 hover:bg-background-100'
            }`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-image-add-line text-xl" />
            </span>
            <span className="mt-2 text-sm font-medium text-foreground-800">
              Toque para escolher ou arraste
            </span>
            <span className="mt-1 text-xs text-secondary-500">jpg, png ou webp · até 5 MB</span>
          </div>
        )}
      </div>

      {/* Ajustes avançados (recolhido) */}
      <div className="rounded-lg border border-secondary-200 bg-white">
        <button
          type="button"
          onClick={() => setMostrarAjustes((v) => !v)}
          className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100"
        >
          <span className="whitespace-nowrap">Ajustes avançados</span>
          <span className="flex h-5 w-5 items-center justify-center">
            <i className={mostrarAjustes ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </span>
        </button>

        {mostrarAjustes && (
          <div className="space-y-5 border-t border-secondary-200 px-4 py-5">
            <CampoArea
              id="descricao"
              name="descricao"
              label="Descrição longa"
              help={
                ehIngresso
                  ? 'Explique de forma simples o que é o evento, para quem é e o que a pessoa precisa saber antes de comprar o ingresso.'
                  : 'Explique de forma simples o que é o evento, para quem é e o que a pessoa precisa saber antes de se inscrever.'
              }
              value={draft.descricao}
              onChange={(v) => mudar('descricao', v)}
              placeholder="Um fim de semana de oração, convivência e formação para jovens de 14 a 25 anos…"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_150px]">
              <CampoTexto
                id="endereco"
                name="endereco"
                label="Endereço"
                help="O endereço exato, do jeito que o Google Maps encontra."
                value={draft.endereco}
                onChange={(v) => mudar('endereco', v)}
                placeholder="Rua das Flores, 120 - Centro"
              />
              <CampoTexto
                id="cep"
                name="cep"
                label="CEP"
                value={draft.cep}
                onChange={(v) => mudar('cep', v)}
                placeholder="17600-000"
                inputMode="numeric"
              />
            </div>
          </div>
        )}
      </div>

      {/* Indicador de salvamento automático */}
      <div className="flex min-h-5 items-center gap-1.5 text-xs text-secondary-500">
        {estadoSalvamento === 'salvando' && (
          <>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-loader-4-line animate-spin" />
            </span>
            salvando…
          </>
        )}
        {estadoSalvamento === 'salvo' && (
          <>
            <span className="flex h-3.5 w-3.5 items-center justify-center text-green-600">
              <i className="ri-check-line" />
            </span>
            salvo automaticamente como rascunho
          </>
        )}
        {estadoSalvamento === 'offline' && (
          <>
            <span className="flex h-3.5 w-3.5 items-center justify-center text-amber-600">
              <i className="ri-wifi-off-line" />
            </span>
            sem internet — vou salvar quando voltar
          </>
        )}
      </div>
    </div>
  );
}

export default forwardRef(BlocoBasico);